import React from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import '../styles/driver-onboarding-guide.css';

type GuideStep = {
  id: string;
  eyebrow: string;
  title: string;
  body: string;
  tips: string[];
  image?: string;
  imageAlt?: string;
};

const imageBase = '/images/driver-onboarding-guide';

const guideSteps: GuideStep[] = [
  {
    id: 'welcome',
    eyebrow: 'Before signup',
    title: 'Know what ShiftWagon is for',
    body:
      'The first screens explain the driver app: earn with deliveries, receive smart offers, follow clear routes, and share trip progress safely.',
    tips: [
      'Read the three intro cards if this is your first time using the app.',
      'Tap Get Started when you are ready to create your driver account.',
    ],
    image: `${imageBase}/03-welcome-support.jpeg`,
    imageAlt: 'ShiftWagon onboarding welcome card for safety and support.',
  },
  {
    id: 'account',
    eyebrow: 'Account',
    title: 'Create your account',
    body:
      'Enter your full name, email address, and a strong password. You can also choose Google signup.',
    tips: [
      'Use the same name that appears on your driver documents.',
      'Use an email address you can open immediately because the next step sends a code.',
      'Your password must be at least 8 characters and include uppercase, lowercase, and a number.',
    ],
    image: `${imageBase}/04-create-account.jpeg`,
    imageAlt: 'Create your ShiftWagon driver account screen.',
  },
  {
    id: 'operating-town',
    eyebrow: 'Operating area',
    title: 'Choose your operating town',
    body:
      'Select South Africa, choose your province, then search for the town where you usually operate. ShiftWagon uses this to match drivers with nearby customers.',
    tips: [
      'Choose a town from the search suggestions so the app can save accurate coordinates.',
      'If your exact town is missing, try a nearby suburb or municipality.',
      'Check the selected town before tapping Create Account.',
    ],
    image: `${imageBase}/05-operating-town.jpeg`,
    imageAlt: 'Operating town screen with Midrand selected.',
  },
  {
    id: 'email',
    eyebrow: 'Email verification',
    title: 'Verify your email',
    body:
      'The app sends a 6 digit code to your email. Enter the code to continue onboarding.',
    tips: [
      'Check your inbox and spam folder.',
      'Use Resend code if the email does not arrive.',
      'If the email address is wrong, tap Start again and create the account with the correct email.',
    ],
    image: `${imageBase}/06-verify-email.jpeg`,
    imageAlt: 'Email verification screen with six empty code inputs.',
  },
  {
    id: 'consents',
    eyebrow: 'Privacy and terms',
    title: 'Accept the required driver consents',
    body:
      'Review the policies and required acknowledgements. These explain how the app uses documents, contact details, location, trip records, customer information, and payout information.',
    tips: [
      'Required items must be ticked before you can continue.',
      'Location consent matters because customers and operations need live trip progress while you are online or on a delivery.',
      'Marketing updates are optional if shown.',
    ],
    image: `${imageBase}/07-driver-consents.jpeg`,
    imageAlt: 'Driver privacy and terms screen with required consent items.',
  },
  {
    id: 'phone',
    eyebrow: 'Phone verification',
    title: 'Verify your mobile number',
    body:
      'Enter the phone number you will use for trip updates and account security. The app sends a code and verifies automatically after all 6 digits are entered.',
    tips: [
      'Start with the country code, for example 27 for South Africa.',
      'Use a number you will keep active while driving.',
      'Tap Edit number if you typed the wrong number.',
    ],
    image: `${imageBase}/08-phone-number.jpeg`,
    imageAlt: 'Phone number entry screen for driver verification.',
  },
  {
    id: 'face-liveness',
    eyebrow: 'Identity check',
    title: 'Complete face liveness',
    body:
      'The app asks for a short guided face check. This confirms that the real driver is completing onboarding.',
    tips: [
      'Use good lighting and keep your face inside the guide circle.',
      'Complete the center, left, and right checks.',
      'Do this yourself. Do not ask someone else to complete the face check for your account.',
    ],
  },
  {
    id: 'personal-docs',
    eyebrow: 'Personal documents',
    title: 'Upload your ID, criminal record file, and half photo',
    body:
      'Upload your ID copy as a PDF, your criminal record verification as a PDF, and a clear half photo as an image.',
    tips: [
      'After uploading the criminal record file, enter the issue date shown on the document.',
      'You can replace uploaded files before final submission.',
    ],
    image: `${imageBase}/10-personal-docs-empty.jpeg`,
    imageAlt: 'Personal documents upload screen with ID copy, criminal record, and half photo.',
  },
  {
    id: 'vehicle-docs',
    eyebrow: 'Vehicle documents and photos',
    title: 'Add vehicle photos first, then vehicle documents',
    body:
      'Take clear photos of the front, left side, right side, and back of the vehicle. Then upload the required vehicle documents and add the dates printed on them.',
    tips: [
      'Use good lighting and keep the full vehicle in frame.',
      'Make number plates visible where possible.',
      'Prepare your own vehicle documents, including roadworthy paperwork, licence disk, PDP, and insurance where required.',
    ],
    image: `${imageBase}/13-vehicle-docs-overview.jpeg`,
    imageAlt: 'Vehicle documents and photos overview screen.',
  },
  {
    id: 'vehicle-dates',
    eyebrow: 'Document dates',
    title: 'Enter document dates exactly as shown',
    body:
      'Some documents ask for an issued-on date, a valid-to date, or both. The app formats the date as YYYY-MM-DD.',
    tips: [
      'The licence disk is the vehicle licence label displayed on the windscreen. Take a clear photo where the printed dates can be read.',
      'For the licence disk, enter both issue date and expiry date.',
      'For the PDP and roadworthy document, use the dates printed on the document.',
      'If a date is unclear, ask your ShiftWagon representative before submitting.',
    ],
    image: `${imageBase}/16-licence-disk-dates.jpeg`,
    imageAlt: 'Licence disk dates modal with issued-on and valid-to fields.',
  },
  {
    id: 'insurance',
    eyebrow: 'Insurance',
    title: 'Choose full cover or restricted access',
    body:
      'Goods in Transit insurance can unlock wider access. If you do not have it yet, the app can let you continue with restricted access for lower-risk loads until valid cover is verified.',
    tips: [
      'Upload Goods in Transit proof if you already have it.',
      'If you continue without GIT, understand that some jobs may not be available.',
      'The separate business-use insurance screen will be added here once the final screenshot is ready.',
    ],
    image: `${imageBase}/17-git-restricted.jpeg`,
    imageAlt: 'Goods in Transit insurance section with restricted access enabled.',
  },
  {
    id: 'account-type',
    eyebrow: 'Driver setup',
    title: 'Choose how you drive',
    body:
      'Choose Individual Owner if you operate for yourself, or Company Driver if you drive under a fleet, employer, or transport company.',
    tips: [
      'Individual owners provide bank details for their own payout setup and can see their earnings in the driver app.',
      'Company drivers provide the company details their operator gave them.',
      'Company-driver trip earnings and settlement visibility belong to the company owner account, not the individual driver profile.',
      'Choose the lane that matches how you will be paid and managed on ShiftWagon.',
    ],
    image: `${imageBase}/18-account-type.jpeg`,
    imageAlt: 'Account type selection screen showing driver setup options.',
  },
  {
    id: 'company-details',
    eyebrow: 'Final step',
    title: 'Add company or payout details',
    body:
      'If you chose Company Driver, enter the company profile details ShiftWagon should use for this driver account. If you chose Individual Owner, enter the bank details used for your own payout setup.',
    tips: [
      'Company drivers should use the fleet or company name, email, and phone number supplied by their operator.',
      'Individual owners should use the bank account that Paystack should use for split settlement after completed trips.',
      'Company owner dashboards show company-driver performance and settlement visibility after the company profile is claimed.',
      'Enter phone numbers without the plus sign if the company details screen asks for that format.',
    ],
    image: `${imageBase}/20-company-details.jpeg`,
    imageAlt: 'Company details form for company drivers.',
  },
  {
    id: 'review',
    eyebrow: 'After submission',
    title: 'Wait while ShiftWagon reviews your profile',
    body:
      'After registration is submitted, ShiftWagon reviews your documents and verifies your profile. Review usually takes 24 to 48 hours.',
    tips: [
      'You can open Home while waiting, but online availability stays locked until approval.',
      'When approved, the online swipe unlocks and you can start receiving delivery requests.',
      'If something is rejected or unclear, use the app and your representative to correct it quickly.',
    ],
    image: `${imageBase}/22-profile-under-review.jpeg`,
    imageAlt: 'Home screen showing profile under review and online availability locked.',
  },
];

const proofItems = [
  'ID copy as a PDF',
  'Half photo as an image',
  'Vehicle photos: front, left, right, and back',
  'Licence disk photo from the windscreen, with readable dates',
  'PDP file or photo and dates',
  'Goods in Transit proof, or restricted access confirmation',
  'Company name, email, and phone number for company drivers',
];

const DriverOnboardingGuidePage: React.FC = () => {
  return (
    <div className="driver-guide-shell">
      <Header />
      <main>
        <section className="driver-guide-hero">
          <div className="container driver-guide-hero-inner">
            <div className="driver-guide-hero-copy">
              <p className="driver-guide-kicker">Driver onboarding guide</p>
              <h1>Get approved to drive with ShiftWagon</h1>
              <p>
                Follow these steps in the driver app. The goal is simple: create your account,
                upload the right documents, wait for review, then go online when approved.
              </p>
              <div className="driver-guide-hero-actions" aria-label="Guide shortcuts">
                <a className="driver-guide-primary-link" href="#steps">
                  Start the guide
                </a>
                <a className="driver-guide-secondary-link" href="#checklist">
                  View checklist
                </a>
              </div>
            </div>
            <div className="driver-guide-phone-stack" aria-hidden="true">
              <img
                src={`${imageBase}/23-home-online.jpeg`}
                alt=""
                className="driver-guide-hero-shot"
                loading="eager"
              />
            </div>
          </div>
        </section>

        <section className="driver-guide-section" id="steps">
          <div className="container">
            <div className="driver-guide-section-heading">
              <p className="driver-guide-kicker">Step by step</p>
              <h2>What to do in the driver app</h2>
            </div>

            <div className="driver-guide-steps">
              {guideSteps.map((step, index) => (
                <article className="driver-guide-step" id={step.id} key={step.id}>
                  <div className="driver-guide-step-copy">
                    <div className="driver-guide-step-meta">
                      <span>{String(index + 1).padStart(2, '0')}</span>
                      <strong>{step.eyebrow}</strong>
                    </div>
                    <h3>{step.title}</h3>
                    <p>{step.body}</p>
                    <ul>
                      {step.tips.map((tip) => (
                        <li key={tip}>{tip}</li>
                      ))}
                    </ul>
                  </div>

                  {step.image ? (
                    <figure className="driver-guide-shot-card">
                      <img src={step.image} alt={step.imageAlt} loading="lazy" />
                    </figure>
                  ) : (
                    <div className="driver-guide-placeholder">
                      <span>Face check</span>
                      <p>
                        The app will guide you through center, left, and right face captures
                        before you continue.
                      </p>
                    </div>
                  )}
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="driver-guide-checklist" id="checklist">
          <div className="container driver-guide-checklist-inner">
            <div>
              <p className="driver-guide-kicker">Before you start</p>
              <h2>Keep these ready</h2>
              <p>
                Having the right files ready makes onboarding faster. PDF means upload a document
                file. Image means take a photo or choose a clear image from your gallery.
              </p>
            </div>
            <ul>
              {proofItems.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default DriverOnboardingGuidePage;
